import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { t } from 'i18next';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useQuery } from 'react-query';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

export default memo(function DiscountTable({
  applyDiscount,
  startPrice,
  currency,
  startPricePercentage,
  discount,
  discountPercentage,
  sellPrice,
  priceForMonth,
  month,
  discountId,
  fullPrice,
  setStartPrice,
  setStartPricePercentage,
  setDiscount,
  setDiscountPercentage,
  setSellPrice,
  setPriceForMonth,
  setMonth,
  setPriceForSquare,
  square,
  selectedDiscount,
  toPrint,
  setSelectedDiscount = () => {},
  setStartPriceTopOrder = () => {},
  defaultStartPrice = 0,
  filters,
  setFilters,
  lastPayment,
  isCreditTable,
  isSubsidy,
  calculations,
  subsidyCals,
}) {
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);

  const { isLoading, isFetching, isError, error, data } = useQuery({
    queryKey: ['/admin/discount/edit/{tabIndex}', discountId],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/admin/discount/edit/${discountId}`,
      );
      return response?.data?.data;
    },
    onSuccess: (res) => {
      const { discounts } = res;
      if (discounts && discounts.length) {
        const discount = discounts.find(
          (discount) => discount?.payment == defaultStartPrice,
        );

        if (discount) {
          onClick(discount);
        }
      }
    },

    enabled: !hasError && !!discountId && applyDiscount,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const discounts = useMemo(() => {
    const list = data?.discounts;
    if (filters?.month && filters?.payment) {
      return list?.filter(
        (dis) =>
          dis?.month == filters?.month && dis?.payment == filters?.payment,
      );
    }
    if (filters?.month) {
      return list?.filter((dis) => dis?.month == filters?.month);
    }
    if (filters?.payment) {
      return list?.filter((dis) => dis?.payment == filters?.payment);
    }
    return list;
  }, [data, filters]);

  useEffect(() => {
    if (!applyDiscount) {
      setSelectedDiscount({});
    } else if (Object.keys(selectedDiscount)?.length) {
      onClick(selectedDiscount);
    }
  }, [applyDiscount, fullPrice]);

  const discountPriceDis = useCallback(
    (discount) => {
      return (fullPrice * discount) / 100;
    },
    [fullPrice],
  );

  const sellPriceDis = useCallback(
    (discount, payment) => {
      if (data?.type == 1 && payment < 100) {
        if (payment + discount >= 100) return fullPrice;
      }
      return fullPrice * (1 - discount / 100);
    },
    [fullPrice, data?.type],
  );

  const startPriceDis = useCallback(
    (discount, payment) => {
      if (payment >= 100) return fullPrice * (1 - discount / 100);
      if (data?.type == 0) {
        return ((fullPrice - (fullPrice * discount) / 100) * payment) / 100;
      }
      if (data?.type == 1) {
        return (fullPrice * payment) / 100;
      }
    },
    [data?.type, fullPrice],
  );

  const remainingPrice = useCallback(
    (discount, payment) => {
      return sellPriceDis(discount, payment) - startPriceDis(discount, payment);
    },
    [sellPriceDis, startPriceDis],
  );

  const onClick = (discount) => {
    setStartPriceTopOrder(data?.type == 1);
    setSelectedDiscount(discount);
    setStartPrice(startPriceDis(discount?.discount, discount?.payment));
    setStartPricePercentage(discount?.payment);
    setDiscount(discountPriceDis(discount?.discount));
    setDiscountPercentage(discount?.discount);
    setSellPrice(sellPriceDis(discount?.discount, discount?.payment));
    setPriceForMonth(
      discount?.month
        ? (sellPriceDis(discount?.discount, discount?.payment) -
            startPriceDis(discount?.discount, discount?.payment)) /
            discount?.month
        : '0',
    );
    setPriceForSquare(
      sellPriceDis(discount?.discount, discount?.payment) / square,
    );
    setMonth(discount?.month);
  };

  const priceFormMonth = useCallback(
    (remainingPrice, month) => {
      if (remainingPrice < lastPayment) return 0;
      return (remainingPrice - lastPayment) / month;
    },
    [lastPayment],
  );

  return (
    <TableContainer className='calculation-table relative z-10 !shadow-none !base-border !bg-white'>
      <Table aria-labelledby='tableTitle'>
        <TableHead>
          <TableRow className='!whitespace-nowrap !bg-[#F9F9F9] [&>*>span]:font-medium'>
            <TableCell className='!px-2'>
              <span className='text-small'>
                {t('shaxmatka.moreHomeDetails.startPrice')}
                {/* ({currency == '1' ? '$' : 'UZS'}) */}
              </span>
            </TableCell>
            <TableCell className='!px-2'>
              <span className='text-small'>
                {t('shaxmatka.moreHomeDetails.discount')}
                {/* ({currency == '1' ? '$' : 'UZS'}) */}
              </span>
            </TableCell>
            <TableCell className='!px-2'>
              <span className='text-small'>
                {t('shaxmatka.moreHomeDetails.sellPrice')}
                {/* ({currency == '1' ? '$' : 'UZS'}) */}
              </span>
            </TableCell>
            <TableCell className='!px-2'>
              <span className='text-small'>
                {t('shaxmatka.moreHomeDetails.remainingPayment')}
                {/* ({currency == '1' ? '$' : 'UZS'}) */}
              </span>
            </TableCell>
            <TableCell className='!px-2'>
              <span className='text-small'>
                {t('shaxmatka.moreHomeDetails.month')}
              </span>
            </TableCell>
            {!applyDiscount && isCreditTable ? (
              <TableCell className='!px-2'>
                <span className='text-small'>
                  {t('shaxmatka.moreHomeDetails.monthlyCreditPrice')}
                  {/* ({currency == '1' ? '$' : 'UZS'}) */}
                </span>
              </TableCell>
            ) : (
              <TableCell className='!px-2'>
                <span className='text-small'>
                  {t('shaxmatka.moreHomeDetails.monthlyPrice')}
                  {/* ({currency == '1' ? '$' : 'UZS'}) */}
                </span>
              </TableCell>
            )}
            {!!lastPayment && !isCreditTable && (
              <TableCell className='!px-2'>
                <span className='text-small'>
                  {t('common.fields.lastPayment')}
                </span>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody className='overflow-hidden'>
          {applyDiscount ? (
            isLoading || isFetching ? (
              <TableRow>
                <TableCell
                  colSpan={lastPayment ? 7 : 6}
                  sx={{ padding: 0, paddingBottom: '50px' }}
                >
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={lastPayment ? 7 : 6}>
                  <div className='flex flex-col items-center'>
                    {error?.response?.data?.message && (
                      <span className='text-red-600 font-medium'>
                        {error?.response?.data?.message}
                      </span>
                    )}
                    {error?.response?.data?.details &&
                      error?.response?.data?.details[0]?.message && (
                        <div>
                          <span className='text-red-600 font-medium'>
                            {t('common.errors.message')}
                          </span>
                          <span>
                            {error?.response?.data?.details[0]?.message}
                          </span>
                        </div>
                      )}
                  </div>
                </TableCell>
              </TableRow>
            ) : discounts?.length > 0 ? (
              discounts?.map((item, i) => (
                <TableRow
                  key={i}
                  hover
                  tabIndex={-1}
                  onClick={() => onClick(item)}
                >
                  <TableCell
                    className={`!whitespace-nowrap cell-text !px-2 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    } ${
                      selectedDiscount?.id === item?.id ? '!text-green-700' : ''
                    }`}
                  >
                    <NumericFormat
                      value={startPriceDis(item?.discount, item?.payment)}
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-right text-[13px]'
                      // suffix={currency == '1' ? '-$ ' : '-UZS '}
                      suffix=''
                    />
                    &nbsp;({item?.payment}%)
                  </TableCell>
                  <TableCell
                    className={`!whitespace-nowrap cell-text !px-2 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    } ${
                      selectedDiscount?.id === item?.id ? '!text-green-700' : ''
                    }`}
                  >
                    <NumericFormat
                      value={discountPriceDis(item?.discount)}
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-right text-[13px]'
                      // suffix={currency == '1' ? '-$ ' : '-UZS '}
                      suffix=''
                    />
                    &nbsp;({item?.discount}%)
                  </TableCell>
                  <TableCell
                    className={`!whitespace-nowrap cell-text !px-2 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    } ${
                      selectedDiscount?.id === item?.id ? '!text-green-700' : ''
                    }`}
                  >
                    <NumericFormat
                      value={sellPriceDis(item?.discount, item?.payment)}
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-right text-[13px]'
                      // suffix={currency == '1' ? '-$' : '-UZS'}
                      suffix=''
                    />
                  </TableCell>
                  <TableCell
                    className={`!whitespace-nowrap cell-text !px-2 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    } ${
                      selectedDiscount?.id === item?.id ? '!text-green-700' : ''
                    }`}
                  >
                    <NumericFormat
                      value={remainingPrice(
                        item?.discount,
                        item?.payment,
                        item?.startPrice,
                      )}
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-right text-[13px]'
                      // suffix={currency == '1' ? '-$' : '-UZS'}
                      suffix=''
                    />
                  </TableCell>
                  <TableCell
                    className={`!whitespace-nowrap cell-text !px-2 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    } ${
                      selectedDiscount?.id === item?.id ? '!text-green-700' : ''
                    }`}
                  >
                    {item?.month}
                  </TableCell>
                  <TableCell
                    className={`!whitespace-nowrap cell-text !px-2 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    } ${
                      selectedDiscount?.id === item?.id ? '!text-green-700' : ''
                    }`}
                  >
                    <NumericFormat
                      value={
                        item?.month
                          ? lastPayment
                            ? priceFormMonth(
                                remainingPrice(
                                  item?.discount,
                                  item?.payment,
                                  item?.startPrice,
                                ),
                                item?.month,
                              )
                            : (sellPriceDis(item?.discount, item?.payment) -
                                startPriceDis(item?.discount, item?.payment)) /
                              item?.month
                          : '0'
                      }
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-right text-[13px]'
                      // suffix={currency == '1' ? '-$' : '-UZS'}
                      suffix=''
                    />
                  </TableCell>
                  {!!lastPayment && (
                    <TableCell
                      className={`!whitespace-nowrap cell-text !px-2 ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-700'
                          : ''
                      }`}
                    >
                      {item?.payment == 100 ? (
                        '0'
                      ) : (
                        <NumericFormat
                          value={
                            lastPayment >
                            remainingPrice(
                              item?.discount,
                              item?.payment,
                              item?.startPrice,
                            )
                              ? remainingPrice(
                                  item?.discount,
                                  item?.payment,
                                  item?.startPrice,
                                )
                              : lastPayment
                          }
                          allowNegative={false}
                          displayType={'text'}
                          thousandSeparator={' '}
                          decimalScale={1}
                          className='w-full text-right text-green-700 text-[13px]'
                          suffix=''
                        />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <span className='no-data-found-wrapper'>
                    <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                    {t('common.global.noDataFound')}
                  </span>
                </TableCell>
              </TableRow>
            )
          ) : (
            <TableRow hover tabIndex={-1}>
              <TableCell
                className={`cell-text text-[13px] !px-2 ${
                  toPrint ? '!py-2 !whitespace-nowrap' : ''
                }`}
              >
                <NumericFormat
                  value={startPrice}
                  allowNegative={false}
                  displayType={'text'}
                  thousandSeparator={' '}
                  decimalScale={1}
                  className='w-full text-green-700 text-right text-[13px]'
                  // suffix={currency == '1' ? '-$ ' : '-UZS '}
                  suffix=''
                />
                <span className='!text-green-700'>
                  &nbsp;({startPricePercentage}%)
                </span>
              </TableCell>
              <TableCell
                className={`cell-text text-[13px] !px-2 ${
                  toPrint ? '!py-2 !whitespace-nowrap' : ''
                }`}
              >
                <NumericFormat
                  value={discount}
                  allowNegative={false}
                  displayType={'text'}
                  thousandSeparator={' '}
                  decimalScale={1}
                  className='w-full text-green-700 text-right text-[13px]'
                  // suffix={currency == '1' ? '-$' : '-UZS'}
                  suffix=''
                />
                <span className='!text-green-700'>
                  &nbsp;({discountPercentage}%)
                </span>
              </TableCell>
              <TableCell
                className={`cell-text text-[13px] !px-2 ${
                  toPrint ? '!py-2 !whitespace-nowrap' : ''
                }`}
              >
                <NumericFormat
                  value={sellPrice}
                  allowNegative={false}
                  displayType={'text'}
                  thousandSeparator={' '}
                  decimalScale={1}
                  className='w-full text-green-700 text-right text-[13px]'
                  // suffix={currency == '1' ? '-$' : '-UZS'}
                  suffix=''
                />
              </TableCell>
              <TableCell
                className={`cell-text text-[13px] !px-2 ${
                  toPrint ? '!py-2 !whitespace-nowrap' : ''
                }`}
              >
                <NumericFormat
                  value={sellPrice - startPrice - (lastPayment || 0)}
                  allowNegative={false}
                  displayType={'text'}
                  thousandSeparator={' '}
                  decimalScale={2}
                  className='w-full text-right text-[13px] text-green-700'
                  // suffix={currency == '1' ? '-$' : '-UZS'}
                  suffix=''
                />
              </TableCell>
              <TableCell
                className={`cell-text text-[13px] !px-2 ${
                  toPrint ? '!py-2 !whitespace-nowrap' : ''
                }`}
              >
                <span className='!text-green-700'>
                  {isCreditTable ? 240 : month}
                </span>
              </TableCell>
              <TableCell
                className={`cell-text text-[13px] !px-2 ${
                  toPrint ? '!py-2 !whitespace-nowrap' : ''
                }`}
              >
                {isCreditTable ? (
                  <div className='flex flex-col items-start text-left'>
                    <span>
                      {isSubsidy ? <>kr-&nbsp;&nbsp;</> : ''}
                      <NumericFormat
                        value={+calculations?.monthlyPayment}
                        allowNegative={false}
                        displayType={'text'}
                        thousandSeparator={' '}
                        decimalScale={2}
                        className='w-full text-green-700 text-[13px]'
                        // suffix={currency == '1' ? '-$' : '-UZS'}
                        suffix=''
                      />
                    </span>
                    {isSubsidy ? (
                      <span>
                        sb-&nbsp;
                        <NumericFormat
                          value={+subsidyCals?.monthlyPayment}
                          allowNegative={false}
                          displayType={'text'}
                          thousandSeparator={' '}
                          decimalScale={2}
                          className='w-full text-green-700 text-[13px]'
                          // suffix={currency == '1' ? '-$' : '-UZS'}
                          suffix=''
                        />
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <NumericFormat
                    value={
                      lastPayment
                        ? (sellPrice - startPrice - lastPayment) / month
                        : priceForMonth
                    }
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-green-700 text-right text-[13px]'
                    // suffix={currency == '1' ? '-$' : '-UZS'}
                    suffix=''
                  />
                )}
              </TableCell>
              {!!lastPayment && !isCreditTable && (
                <TableCell
                  className={`cell-text text-[13px] !px-2 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  {startPricePercentage == 100 ? (
                    '0'
                  ) : (
                    <NumericFormat
                      value={lastPayment}
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-right text-[13px] text-green-700'
                      suffix=''
                    />
                  )}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
